import { Injectable, NgZone } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { LocLabel } from '../components/clickable-row-table/models/data-table-config.models';
import { LocalizationHelper } from '../helpers/localization.helper';
import { LocalizationService } from '../services/localization.service';
import { SentryService } from '../services/sentry.service';
import { SnackBarService } from '../services/snackBar.service';
import { PasswordValidators as Passwd } from '../validators/password.validators';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  // prettier-ignore
  /** 'KEY' means that translation key is 'error.{code}' */
  private readonly errorMappings: { [code: string]: LocLabel | 'KEY' } = {
    required: 'KEY',                email: 'KEY',         phoneNumber: 'KEY',   iban: 'KEY',
    nip: 'KEY',                     regon: 'KEY',         zipCode: 'KEY',       landRegister: 'KEY',
    krs: 'KEY',                     beforeAfter: 'KEY',   dateFromPast: 'KEY',  dateFromFuture: 'KEY',
    matDatepickerMin: 'KEY',        matDatepickerMax: 'KEY', matDatepickerParse: 'KEY',
    matDatepickerFilter: 'KEY',
    matDatetimePickerMin: { key: 'error.matDatepickerMin' },
    matDatetimePickerMax: { key: 'error.matDatepickerMax' },
    matDatetimePickerParse: { key: 'error.matDatepickerParse' },
    matDatetimePickerFilter: { key: 'error.matDatepickerFilter' },
    dateFromFutureOrInitial: 'KEY', uniqueValue: 'KEY',   fileType: 'KEY',      maxFileSize: 'KEY',
    outOfAgrRange: 'KEY',           passwdChar: 'KEY',    passwdDict: 'KEY',    passwdEqual: 'KEY',
    passwdPrev: 'KEY',              negativeValue: 'KEY', shareSum: 'KEY',      atLeastOne: 'KEY',
    timeFromTo: 'KEY',              bookingHours: 'KEY',  permDenied: 'KEY',    oneTenancyPerLandlord: 'KEY',
    minlength: 'KEY',               maxlength: 'KEY',     pattern: 'KEY',       captchaInvalid: 'KEY',
    noValidTarget: 'KEY',           noEmailTarget: 'KEY', noMobileTarget: 'KEY',
    coordInvExists: 'KEY',          offerEventStartPast: 'KEY',
    fixFormErrors:  { key: 'error.fixForm'                                                            },
    passwdCharRep:  { key: 'error.passwdCharRep',   replaceMap: { $amount: Passwd.MAX_REP + 1 }       },
    passwdCharSeq:  { key: 'error.passwdCharSeq',   replaceMap: { $amount: Passwd.MAX_SEQ + 1 }       },
    passwdKeyChain: { key: 'error.passwdKeyChain',  replaceMap: { $amount: Passwd.MAX_KEY_CHAIN + 1 } }
  };
  // handled manually: requiredAny, ambiguousId

  private readonly locHelper = new LocalizationHelper(this.ls);

  constructor(
    private snackBarService: SnackBarService,
    private zone: NgZone,
    private ls: LocalizationService,
    private sentryService: SentryService
  ) {}

  public showGlobalError(errMsg: string | LocLabel, errorObject?: unknown): void {
    const msg = typeof errMsg === 'string' ? StrHlp.capitalize(this.ls.translate(errMsg)) : this.locHelper.locLabel(errMsg);
    this.zone.run(() => this.snackBarService.showError(msg, this.ls.translate('button.hide')));
    console.error(errorObject ?? msg);
    if (errorObject) this.sentryService.captureError(errorObject);
  }

  // TODO make mor generic `showGlobalWarning`
  public showInvalidFormWarning(text?: string): void {
    this.zone.run(() => this.snackBarService.showWarn(text ?? this.getErrorText('fixFormErrors')!));
  }

  /**
   * In production environment sends error to sentry without notifying user, otherwise displays warning in console
   * @param {unknown} error
   */
  public showSilentWarning(error: unknown) {
    if (environment.envName === 'production') {
      this.sentryService.captureError(error);
    } else {
      console.warn('Silent error:', error);
    }
  }

  private getProperty(propertyName: string, object: any) {
    const parts = propertyName.split('.');
    let property = object || this;

    for (let i = 0; i < parts.length; i++) {
      if (property) property = property[parts[i]];
      else return null;
    }

    return property;
  }

  private interpolateString(str: any, dict: any) {
    // eslint-disable-next-line no-useless-escape
    return str.replace(/\{([A-Za-z\._]+)\}/g, (original: any, key: string) => {
      const tokenValue = this.getProperty(key, dict);
      return tokenValue ? tokenValue : original;
    });
  }

  public getErrorText(error: string | ValidationErrors | null): string | null {
    if (error == null) {
      return null;
    } else if (typeof error === 'string') {
      return this.translateError(error);
    } else {
      const keys = Object.keys(error);
      if (keys.length > 0) {
        switch (keys[0]) {
          case 'requiredAny': {
            const $fields = error.requiredAny.fields.map((x: string) => this.ls.translate(x)).join(', ');
            return this.ls.trReplace('error.requiredAny', { $fields });
          }
          case 'ambiguousId':
            return this.ls.trReplace('error.ambiguousId', { $id: error.ambiguousId });
          case 'url': {
            const $domain = error.url.domain;
            return this.ls.trReplace($domain ? 'error.urlDomain' : 'error.url', { $domain });
          }
          // TODO: use validator error properties for more accurate descriptions
          default: {
            const errorMessage = this.translateError(keys[0]);
            return this.interpolateString(errorMessage, error);
          }
        }
      } else {
        return null;
      }
    }
  }

  private translateError(errorKey: string): string {
    const mapping = this.errorMappings[errorKey] ?? '';
    if (mapping == 'KEY') {
      return this.ls.translate(`error.${errorKey}`);
    } else if (!mapping) {
      this.sentryService.captureError(`No mapping for error key '${errorKey}'`);
      return '';
    } else {
      return this.locHelper.locLabel(mapping);
    }
  }
}
